import { Skeleton } from 'antd'
import { ItemType } from 'antd/es/menu/interface'
import dayjs from 'dayjs'
import { apiDateFormatTimeDate } from 'shared/libs/dayjs/model'
import { useHandleRequest } from 'shared/libs/hooks/useHandleRequest/useHandleRequest'
import { getOffset } from 'shared/libs/pagination/offset'

import { useEffect, useMemo } from 'react'

import { useNavigate } from 'react-router-dom'

import { Circle } from 'shared/ui/Circle'
import { Spin } from 'shared/ui/Spin'

import { useAppSelector } from 'shared/config/redux/hooks/useRedux'
import { palette } from 'shared/config/styles/palette'

import {
  useChangeNotificationsViewMutation,
  useLazyNotificationsQuery,
} from '../../services/notifcationsApi'
import { Notification } from '../../types'
import { useNotificationsObserve } from '../useNotificationsObserve'

interface Params {
  isOpenDropdown: boolean
}
export const useAllNotifications = ({ isOpenDropdown }: Params) => {
  const navigate = useNavigate()
  const { handleError } = useHandleRequest()

  const { count, limit, page, notifications, isUpdate } =
    useAppSelector((state) => state.notification) || {}
  const [getNotifications, { isFetching: isNotificationsFetching }] =
    useLazyNotificationsQuery()
  const [
    changeNotificationsView,
    { isLoading: isChangeNotificationViewLoading },
  ] = useChangeNotificationsViewMutation()

  const lastPage = useMemo(
    () => notifications && Number(Object.keys(notifications).sort().at(-1)),
    [notifications],
  )

  const flattedNotifications = useMemo(() => {
    if (!notifications) return []
    return Object.values(notifications).flatMap(
      (ordersPage) => ordersPage?.results || [],
    )
  }, [notifications])

  const { createObserver, nextDataCount, currentPage } =
    useNotificationsObserve({
      count: count ?? null,
      intersectionObserverIndex: 7,
      isFetching: isNotificationsFetching,
      lastPage: lastPage ?? null,
      limit: limit,
      notifications: flattedNotifications,
    })

  const handleClickNotification = async (notification: Notification) => {
    if (!notification.company_slug) return

    if (!notification.has_seen) {
      await changeNotificationsView({
        notifications: [notification.id],
      }).unwrap()
    }

    if (notification.order_id === null) {
      navigate(
        `/dashboard/knowledge-base/${notification.company_slug}/${notification.folder_id ?? ''}`,
      )
    } else if (typeof notification.order_id === 'number') {
      navigate(
        `/dashboard/orders/${notification.company_slug}/${notification.order_id}`,
      )
    }
  }

  const loadingNotifications: ItemType[] = useMemo(() => {
    if (!isNotificationsFetching || !nextDataCount || isUpdate) return []
    return Array.from({ length: nextDataCount }, (_, index) => ({
      label: (
        <Skeleton.Node
          active
          fullSize
          className="!h-[55px] !w-full"
          rootClassName="!h-auto !py-4 !px-3 border-b border-light_gray_3"
        />
      ),
      key: `loading-${index}`,
      disabled: true,
    })) as ItemType[]
  }, [isNotificationsFetching, nextDataCount])

  const notificationsData: ItemType[] = useMemo(() => {
    return flattedNotifications.map((notification, index) => ({
      key: notification.id,
      label: (
        <div
          className="flex gap-2 items-center py-4 px-3 border-b border-light_gray_3"
          ref={limit ? createObserver(index) : null}
          key={notification.id}
          data-index={index}
          data-id={notification.id}
          data-seen={notification.has_seen ? notification.has_seen : null}
        >
          {!notification.has_seen &&
            (!isChangeNotificationViewLoading ? (
              <Circle
                size={10}
                sameWrapperSize
                color={palette.red_2}
              />
            ) : (
              <Spin
                size="small"
                className="flex items-center justify-center w-2.5 h-2.5"
              />
            ))}
          <div className="flex-1 flex flex-col gap-1">
            <div className="flex-1 flex gap-2 justify-between items-start">
              <span className="text-dark leading-[26px] text-[18px] font-medium">
                {notification.title}
              </span>
              <span className="text-light_gray_4 leading-[14px] text-[11px] ">
                {dayjs(notification.created_date)
                  .format(apiDateFormatTimeDate)
                  .replace('.', '')}
              </span>
            </div>
            <span className="text-dark leading-6 text-base">
              {notification.content}
            </span>
          </div>
        </div>
      ),
      onClick: (e) => {
        e.domEvent.stopPropagation()
        handleClickNotification(notification)
      },
    })) as ItemType[]
  }, [notifications])

  const handleGetNotifications = async ({
    page,
    limit,
  }: {
    page: number
    limit: number
  }) => {
    try {
      await getNotifications({
        params: {
          limit,
          offset: getOffset(page, limit),
        },
      }).unwrap()
    } catch (err) {
      handleError(err)
    }
  }

  useEffect(() => {
    if (page && limit && isOpenDropdown && typeof isUpdate === 'boolean') {
      handleGetNotifications({
        page,
        limit,
      })
    }
  }, [page, limit, isOpenDropdown, isUpdate])

  return {
    notificationsData,
    notficationsLimit: limit,
    nextDataCount,
    isNotificationsFetching,
    loadingNotifications,
    currentPage,
    allPages: notifications && Object.keys(notifications),
  }
}
